import { ReactElement, useEffect, useState } from "react";
import {
  FormControl,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Autocomplete
} from "@mui/material";
import StyledTableRow from "../../../../../components/styledTableRow";
import { Add, Delete } from "@mui/icons-material";
import { getPalette, getPaletteColor } from "../../../../../configuration/configuration";
import { getUID } from "../../../../../system/utils";
import { useLazyFetch } from "../../../../../system/useFetch";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import TableContainer from "../../../../../components/tableContainer";
import { IDashboardComponent, IRadarConfig, IRadarVariable } from "../../../types";
import { IAcquisitionVariableFullData } from "../../../../../types";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VariableConfigDialog from "./variableConfigDialog";
import ColorPicker from "../../../../../components/colorPicker";

interface IProps {
  component: IDashboardComponent<IRadarConfig>;
  updateComponent: (component: IDashboardComponent<IRadarConfig>) => void;
  zoneID: number;
}


const maxVar = 15;

interface IVariableRowProps {
  index: number;
  machines: string[];
  machineVariables: Record<string, IAcquisitionVariableFullData[]>;
  data: IRadarVariable;
  setData: (data: IRadarVariable) => void;
  onRemoveVariable: (index: number) => void;
}

interface IDialogContext {
  open: boolean;
  selectedVariable: IRadarVariable | null;
  metaData: IAcquisitionVariableFullData | null;
}


const VariableRow = ({ index, machineVariables, machines, data, setData, onRemoveVariable }: IVariableRowProps): ReactElement => {
  const [ selectedColor, setSelectedColor ] = useState<string>(data.color ?? getPalette().list[index]);
  const [ dialogContext, setDialogContext ] = useState<IDialogContext>({ open: false, selectedVariable: null, metaData: null });

  let units = [ "" ];
  const found = machineVariables[data.machine]?.find(x => x.code === data.code) ?? null;
  if (found && found.axisUnit) {
    units = [ found.axisUnit ];
  }

  if(data.unit && !units.find(u => u === data.unit)){
    units.push(data.unit);
  }

  const updateItemsSeries = (field: keyof IRadarVariable, value: any) => {

    if (field === "code") {
      const variable = machineVariables[data.machine]?.find(x => x.code === value);
      const changed = data[field] !== value;
      data[field] = value;

      if (variable) {
        data.title = changed ? variable.name : data.title;
        data.unit = variable.axisUnit ?? "";
        data.minimum = variable.axisMinimum;
        data.maximum = variable.axisMaximum;
        data.color = variable.color ?? getPalette().list[index];
        setSelectedColor(data.color);
      }
    } else if (field === "machine"){
      data.machine = value;
      data.code = "";
      data.title = "";
      data.unit = "";
    } else if (field === "color"){
      data[field] = `#${value.hex}`;
      setSelectedColor(value);
    } else if(field === "title") {
      data[field] = value;
    } else if(field === "decimals") {
      data[field] = value;
    }

    setData(data);
  };

  const onAccept = (value: IRadarVariable) => {
    data.limits = value.limits;
    setData(value);
    setDialogContext({ ...dialogContext, open: false });
  };

  const onCancel = () => {
    setDialogContext({ ...dialogContext, open: false });
  };


  return (
    <StyledTableRow hover={true} role="checkbox" tabIndex={-1} >
      <TableCell  style={{ maxWidth: 24, width: 24, padding: 4, textAlign: "right" }}>
        {index + 1}
      </TableCell>

      <TableCell>
        <FormControl className="w-100">
          <Select
            variant="standard"
            value={data.machine}
            displayEmpty={true}
            onChange={(event: SelectChangeEvent) => updateItemsSeries("machine", event.target.value as string)}>
            { machines.map(machine => {
              return <MenuItem key={`unit-${index}-${machine}`} value={machine}>{machine}</MenuItem>;
            })}
          </Select>
        </FormControl>
      </TableCell>

      <TableCell style={{ minWidth: 180, maxWidth: 250 }}>
        <Autocomplete
          options={machineVariables[data.machine]}
          getOptionLabel={(option: IAcquisitionVariableFullData) => option.name}
          value={found}
          openOnFocus={true}
          onChange={(_event: any, newValue: IAcquisitionVariableFullData | null) => {
            updateItemsSeries("code", newValue?.code);
          }}
          renderInput={(params) =>
            <TextField {...params} variant="standard" color={"primary"}/>
          }
        />
      </TableCell>

      <TableCell>
        <FormControl className="w-100">
          <TextField
            variant="standard"
            size="small"
            value={data.title}
            type="text"
            slotProps={{ input: {  inputProps: { min: 0, max: 6 } }}}
            onChange={(event) => updateItemsSeries("title",  event.target.value as string)}
          />
        </FormControl>
      </TableCell>

      <TableCell>
        <FormControl className="w-100">
          <Select
            variant="standard"
            value={data.unit ?? ""}
            displayEmpty={true}
            onChange={(event: SelectChangeEvent) => updateItemsSeries("unit", event.target.value)}>
            { units.map((unit, uIndex) => {
              return <MenuItem key={`unit-${index}-${uIndex}`} value={unit}>{unit}</MenuItem>;
            })}
          </Select>
        </FormControl>
      </TableCell>

      <TableCell>
        <FormControl className="w-100">
          <TextField
            variant="standard"
            size="small"
            value={data.decimals}
            type="number"
            slotProps={{ input: {  inputProps: { min: 0, max: 6 } }}}
            onChange={(event) => updateItemsSeries("decimals",  parseInt(event.target.value as string, 10))}
          />
        </FormControl>
      </TableCell>

      <TableCell>
        <FormControl className="w-100">
          <ColorPicker color={selectedColor} onChange={(color) => updateItemsSeries("color", color)} disableAlpha={true}/>
        </FormControl>
      </TableCell>

      <TableCell>
        <IconButton style={{ padding: 0, margin: 0 }}
          disabled={data.code === ""}
          onClick={() => setDialogContext({ open: true, selectedVariable: data, metaData: found })}>
          <Tooltip title={"Config"}>
            <MoreVertIcon style={{ color: getPalette().statusColor.info.background }}/>
          </Tooltip>
        </IconButton>
      </TableCell>

      <TableCell>
        <IconButton style={{ padding: 0, margin: 0 }} onClick={() => onRemoveVariable(index)}>
          <Tooltip title={"Remove variable"}>
            <Delete style={{ color: getPalette().statusColor.error.background }}/>
          </Tooltip>
        </IconButton>
      </TableCell>

      {dialogContext.open && dialogContext.selectedVariable && dialogContext.metaData &&
        <VariableConfigDialog
          tag={found}
          machineVariables={machineVariables[data.machine]}
          variable={dialogContext.selectedVariable}
          metaData={dialogContext.metaData}
          onAccept={onAccept}
          onCancel={onCancel}
        />}

    </StyledTableRow>
  );
};

const VariablesTable = ({ component, updateComponent, zoneID }: IProps): ReactElement => {
  const [ getMachineVariables, { data } ] = useLazyFetch<IAcquisitionVariableFullData[]>();
  const [ machineVariables, setMachineVariables ] = useState<Record<string, IAcquisitionVariableFullData[]>>({});
  const [ machines, setMachines ] = useState<string[]>([]);

  const variablesLength = component.config.variables.length;

  useEffect(() => {
    getMachineVariables({
      module: "acquisition",
      query: "getVariablesByZone",
      payload: {
        zoneID
      }
    });
  }, [ zoneID ]);

  useEffect(() => {
    const dictionary: Record<string, IAcquisitionVariableFullData[]> = {};

    (data ?? []).forEach(v => {
      if (dictionary[v.machine.name]) {
        dictionary[v.machine.name].push(v);
      } else {
        dictionary[v.machine.name] = [v];
      }
    });

    setMachineVariables(dictionary);
    setMachines(Object.keys(dictionary));

    if (data) {
      component.config.variables.forEach(v => {
        const found = data.find(x => x.code === v.code);
        // v.title = found ? found.name :  v.title;
        v.unit = found ? found.axisUnit :  v.unit;
        v.maximum = found ? found.axisMaximum :  v.maximum;
        v.minimum = found ? found.axisMinimum :  v.minimum;
      });

      updateComponent({ ...component });
    }

  }, [ data ]);

  const addSeries = () => {
    component.config.variables.push({
      hash: getUID(),
      machine: machines[0],
      code: "",
      title: "",
      color: getPaletteColor(),
      unit: "",
      minimum: 0,
      maximum: 100,
      decimals: 0,
      limits: {
        minimum: {
          type: "VALUE",
          value: 0,
          comparison: "<"
        },
        maximum: {
          type: "VALUE",
          value: 0,
          comparison: ">"
        },
        aim: {
          type: "NONE",
          value: 0
        },
      }
    });

    updateComponent({ ...component });
  };

  const onRemoveVariable = (index: number) => {
    component.config.variables.splice(index, 1);
    updateComponent({ ...component });
  };

  const onChange = (value: number) => {
    component.config.scale = value;
    updateComponent({ ...component });
  };

  return (
    <>
      <TextField
        className="m-1"
        label="Scale"
        sx={{ width: 200 }}
        variant="standard"
        size="small"
        value={component.config.scale ?? 1}
        type="number"
        slotProps={{ input: { inputProps: { min: 0.5, max: 1 } }}}
        onChange={(event) => onChange(parseFloat(event.target.value as string))}
      />
      <TableContainer sx={{ maxHeight: 570, width: 1200 }}>
        <Table stickyHeader={true}  size={"small"}>
          <TableHead>
            <TableRow>
              <TableCell className="table-header" style={{ maxWidth: 24, width: 24, padding: 4 }}>
                <IconButton sx={{ padding: 0, margin: 0 }} style={{ maxWidth: 21 }} onClick={addSeries} disabled={variablesLength >= maxVar || machines.length === 0}>
                  <Tooltip title={"Add variable"}>
                    <Add style={{ color: getPalette().statusColor.info.background }}/>
                  </Tooltip>
                </IconButton>
              </TableCell>
              <TableCell className="table-header" style={{ minWidth: 100, maxWidth: 100 }}>
                Machine
              </TableCell>
              <TableCell className="table-header" style={{ minWidth: 180, maxWidth: 250 }}>
                Variable - {variablesLength} / {maxVar}
              </TableCell>
              <TableCell className="table-header" style={{ minWidth: 180 }}>Title</TableCell>
              <TableCell className="table-header" style={{ minWidth: 100 }}>Unit</TableCell>
              <TableCell className="table-header" style={{ minWidth: 100 }}>Decimals</TableCell>
              <TableCell className="table-header" style={{ minWidth: 50, width: 50 }}>Color</TableCell>
              <TableCell className="table-header" style={{ minWidth: 50, width: 50 }}>Config</TableCell>
              <TableCell className="table-header"/>
            </TableRow>
          </TableHead>
          <TableBody>
            {data && machines.length > 0 && component.config.variables.map((row, index) => {
              return (
                <VariableRow key={`series-row-${index}`}
                  onRemoveVariable={onRemoveVariable}
                  machineVariables={machineVariables}
                  machines={machines} data={row} index={index} setData={() => updateComponent({ ...component })} />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default VariablesTable;

